import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { DatiqTemplateService } from '@dat/datiq-components-pkg/template';
import { isString, round } from 'lodash';

import {
    DATIQ_INPUT_PERCENT_PERCISION,
    DATIQ_INPUT_SYNCABLE_TYPES,
    DatiqInputErrorTypes,
    DatiqInputTypes,
    DatiqInputValueTypes,
    emptyDatiqInputErrorMessages,
    IDatiqForm,
    IDatiqInput,
    IDatiqInputErrorMessages,
} from '../models';
import { DatiqValidatorsRegisterService } from './datiq-validators-register.service';

@Injectable({
    providedIn: 'root',
})
export class DatiqInputService {
    constructor(
        private _fb: FormBuilder,
        private _template: DatiqTemplateService,
        private _validatorRegistry: DatiqValidatorsRegisterService,
    ) {}

    hasControl(input: IDatiqInput, formGroup: FormGroup): boolean {
        return !!formGroup.get(input.name);
    }

    mapInputsToFormGroup(
        inputs: IDatiqInput[],
        fg: FormGroup,
        formArray?: FormArray,
        formIndex?: number,
    ): FormGroup {
        const formGroup = !!formArray
            ? (this.createFormGroupForArray(formArray, formIndex) as FormGroup)
            : !!fg
            ? fg
            : this._fb.group({});

        return inputs?.reduce(
            (currentFg: FormGroup, input: IDatiqInput): FormGroup =>
                this.addInputToFormGroup(input, currentFg),
            formGroup,
        );
    }

    createFormGroupForArray(
        formArray: FormArray,
        formIndex: number,
    ): AbstractControl {
        return !!formArray.at(formIndex)
            ? (formArray.at(formIndex) as FormGroup)
            : (this.addNewFormGroupToArray(formArray, formIndex) as FormGroup);
    }

    addNewFormGroupToArray(
        formArray: FormArray,
        formIndex: number,
    ): AbstractControl {
        formArray.insert(formIndex, this._fb.group({}));

        return formArray.at(formIndex);
    }

    addInputToFormGroup(input: IDatiqInput, fg: FormGroup): FormGroup {
        const control: FormControl = this._fb.control(input?.value);

        !!input.disabled ? control.disable() : control.enable();

        fg.addControl(input?.name, control);

        return fg;
    }

    isText(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Text;
    }

    isNumber(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Number;
    }

    isInteger(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Integer;
    }
    isSimpleInteger(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.SimpleInteger;
    }
    isCurrency(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Currency;
    }
    isSimpleCurrency(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.SimpleCurrency;
    }

    isPercent(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Percent;
    }

    isSimplePercent(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.SimplePercent;
    }

    isCheckbox(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Checkbox;
    }

    isSwitch(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Switch;
    }

    isDatepicker(settings: IDatiqInput): boolean {
        return (
            settings?.type === DatiqInputTypes.Date ||
            settings?.type === DatiqInputTypes.DateRange
        );
    }

    isGeo(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Geo;
    }

    isSelect(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Select;
    }

    isChecklist(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Checklist;
    }

    isSlider(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Slider;
    }

    isSliderRange(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.SliderRange;
    }

    isToggleButton(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.ToggleButton;
    }

    isToggle(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Toggle;
    }
    isTimeline(settings: IDatiqInput): boolean {
        return settings?.type === DatiqInputTypes.Timeline;
    }

    getControlFromSettings(
        settings: IDatiqInput,
        formGroup: FormGroup,
    ): AbstractControl {
        return !!formGroup.get(settings?.name)
            ? formGroup.get(settings?.name)
            : this._fb.control(null);
    }

    createForm(): FormGroup {
        return this._fb.group({});
    }

    createControl(settings: IDatiqInput, fg: FormGroup): AbstractControl {
        const validators: ValidatorFn[] = this.createControlValidation(
            settings,
            fg,
        );

        const control: AbstractControl = this.getControlFromSettings(
            settings,
            fg,
        );

        // This adds validator to the form group's input control
        control.setValidators(validators);

        // This sets the value of the input control
        control.setValue(settings.value);

        fg.addControl(settings?.name, control);

        // This updates and sets the form validity
        fg.updateValueAndValidity();

        !!settings?.disabled ? control.disable() : control.enable();

        return control;
    }

    getDefaultValue(settings: IDatiqInput): DatiqInputValueTypes {
        return this.isPercent(settings)
            ? this.getDefaultValuePercent(settings)
            : settings?.value;
    }

    cleanUpValue(
        newValue: DatiqInputValueTypes,
        settings: IDatiqInput,
    ): DatiqInputValueTypes {
        return this.isPercent(settings)
            ? this.getCleanValuePercent(newValue as string, settings)
            : newValue;
    }

    getCleanValuePercent(
        value: string | number,
        settings: IDatiqInput,
    ): number {
        const cleanPercentValue: number =
            this.getCleanValuePercentNumber(value);
        const percision: number = this.getPercisionFromSettings(settings);
        const roundedNumber: number = round(cleanPercentValue / 100, percision);

        return round(roundedNumber, percision);
    }
    getCleanValuePercentNumber(value: string | number): number {
        return isString(value) && value?.indexOf('%') >= 0
            ? parseInt(value.replace('%', ''))
            : !isNaN(+value)
            ? +value
            : null;
    }

    getCleanUpdateValue(
        newValue: DatiqInputValueTypes,
        settings: IDatiqInput,
    ): DatiqInputValueTypes {
        return this.isPercent(settings)
            ? this.getCleanUpdateValuePercent(newValue as string, settings)
            : newValue;
    }

    getDefaultValuePercent(settings: IDatiqInput): number {
        const percision: number = this.getPercisionFromSettings(settings);
        const roundedNumber: number = round(+settings?.value, percision);

        return !isNaN(+settings?.value)
            ? round(roundedNumber * 100, percision)
            : null;
    }

    getCleanUpdateValuePercent(
        newValue: DatiqInputValueTypes,
        settings: IDatiqInput,
    ): number {
        const percision: number = this.getPercisionFromSettings(settings);
        const roundedNumber: number = round(+newValue, percision);

        return !isNaN(+newValue) ? round(roundedNumber * 100, percision) : null;
    }

    setUpForm(formGroup: FormGroup, settings: IDatiqForm): FormGroup {
        const newformGroup: FormGroup = !!formGroup
            ? formGroup
            : this.createForm();

        return newformGroup;
    }

    /**
     * This checks the keys for the settings and if the key matches any of the
     * validation functions (functions with Validation in them) it adds that validation
     * function to the array so that we can add more validations dynamically without
     * having to make a specific object
     *
     * @param settings - settings for this input
     * @returns an array of validator functions
     */
    createControlValidation(
        settings: IDatiqInput,
        formGroup?: FormGroup,
    ): ValidatorFn[] {
        const validators: ValidatorFn[] = Object.keys(settings).reduce(
            (currentValidators: ValidatorFn[], key: string): ValidatorFn[] =>
                this._validatorRegistry.hasValidatorService(key)
                    ? [
                          ...this._validatorRegistry
                              .getValidatorService(key)
                              .addValidator(
                                  settings,
                                  currentValidators,
                                  formGroup,
                              ),
                      ]
                    : [...currentValidators],
            [],
        );
        const inputTypeValidators: ValidatorFn[] = this.addInputTypeValidators(
            settings,
            validators,
            formGroup,
        );

        return [...validators, ...inputTypeValidators];
    }

    addInputTypeValidators(
        settings: IDatiqInput,
        validators: any,
        formGroup: FormGroup<any>,
    ): ValidatorFn[] {
        const dateRangeTypeValidators: ValidatorFn[] =
            settings?.type === DatiqInputTypes.DateRange
                ? this._validatorRegistry
                      .getValidatorService(DatiqInputErrorTypes.DateRange)
                      .addValidator(settings, validators, formGroup)
                : [];
        const geoSuggestionTypeValidators: ValidatorFn[] =
            settings?.type === DatiqInputTypes.Geo
                ? this._validatorRegistry
                      .getValidatorService(
                          DatiqInputErrorTypes.GeoIndeterminate,
                      )
                      .addValidator(settings, validators, formGroup)
                : [];

        // TODO: Update this to make sure that it supports multiple select (@georgepe_dat)
        const selectTypeValidators: ValidatorFn[] =
            settings?.type === DatiqInputTypes.Select &&
            !(settings as any)?.multiple
                ? this._validatorRegistry
                      .getValidatorService(
                          DatiqInputErrorTypes.SelectMatchesOptions,
                      )
                      .addValidator(settings, validators, formGroup)
                : [];

        return [
            ...validators,
            ...dateRangeTypeValidators,
            ...geoSuggestionTypeValidators,
            ...selectTypeValidators,
        ];
    }

    getPercisionFromSettings(settings: IDatiqInput): number {
        return !isNaN(settings?.percentPercision)
            ? settings?.percentPercision
            : DATIQ_INPUT_PERCENT_PERCISION;
    }

    getErrorMessagesFromSettings(
        settings: IDatiqInput,
    ): IDatiqInputErrorMessages {
        const errorMessages: IDatiqInputErrorMessages =
            !!settings?.errorMessages ? { ...settings.errorMessages } : {};

        return {
            ...emptyDatiqInputErrorMessages,
            ...errorMessages,
        };
    }

    getErrorMessage(
        inputControl: AbstractControl,
        errorMessages: IDatiqInputErrorMessages,
    ): string {
        const errorsInput: ValidationErrors = !!inputControl?.errors
            ? { ...inputControl.errors }
            : {};
        const errorMessage: string = Object.keys(errorsInput)?.reduce(
            (message: string, errorKey: string): string =>
                !!errorMessages?.[errorKey]
                    ? errorMessages?.[errorKey]
                    : message,
            '',
        );

        return this._template.render(errorMessage, errorsInput);
    }

    isSyncable(settings: IDatiqInput): boolean {
        return DATIQ_INPUT_SYNCABLE_TYPES.some(
            (type: DatiqInputTypes) => type === settings?.type,
        );
    }

    shouldSync(
        inputValue: DatiqInputValueTypes,
        updateValue: DatiqInputValueTypes,
        settings: IDatiqInput,
    ): boolean {
        const newValue: DatiqInputValueTypes = this.cleanUpValue(
            updateValue,
            settings,
        );

        return inputValue !== newValue;
    }
}
